<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-6 max-w-[1700px] mx-auto">

    <!-- Page header -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Sales Orders</h1>
        <p class="text-sm text-gray-500 mt-1">Search, filter and manage customer orders</p>
      </div>
      <div class="flex flex-wrap items-center gap-2 sm:gap-3">
        <div class="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
          <span class="h-2 w-2 rounded-full bg-amber-400"></span>
          <span class="text-sm font-semibold text-gray-700">{{ count }}</span>
          <span class="text-sm text-gray-400">orders</span>
        </div>
        @if (cartTotals) {
        <button
          class="group flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full border shadow-sm transition-all hover:shadow-md"
          [class]="cartTotals.count ? 'bg-violet-50 border-violet-200 hover:border-violet-300' : 'bg-white border-gray-200'"
          (click)="cartsOpen = true"
          matTooltip="Shoppers who reached checkout on the website in the last 30 days, left an email or phone and didn't order - click to follow them up">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [class]="cartTotals.count ? 'text-violet-600' : 'text-gray-400'">remove_shopping_cart</mat-icon>
          <span class="text-sm font-bold" [class]="cartTotals.count ? 'text-violet-700' : 'text-gray-700'">{{ cartTotals.count }}</span>
          <span class="text-sm" [class]="cartTotals.count ? 'text-violet-700/80' : 'text-gray-400'">abandoned carts</span>
          @if (cartTotals.count) { <span class="text-xs font-semibold text-violet-600/80">£{{ cartTotals.value | number:'1.0-0' }}</span> }
        </button>
        }
        @if (sampleTotals) {
        <button
          class="group flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full border shadow-sm transition-all hover:shadow-md"
          [class]="sampleTotals.count ? 'bg-sky-50 border-sky-200 hover:border-sky-300' : 'bg-white border-gray-200'"
          (click)="samplesOpen = true"
          matTooltip="Customers who took free samples in the last 30 days and haven't ordered since - click to follow them up">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [class]="sampleTotals.count ? 'text-sky-600' : 'text-gray-400'">inventory_2</mat-icon>
          <span class="text-sm font-bold" [class]="sampleTotals.count ? 'text-sky-700' : 'text-gray-700'">{{ sampleTotals.count }}</span>
          <span class="text-sm" [class]="sampleTotals.count ? 'text-sky-700/80' : 'text-gray-400'">sample leads</span>
        </button>
        }
        @if (lostTotals) {
        <button
          class="group flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full border shadow-sm transition-all hover:shadow-md"
          [class]="lostTotals.count ? 'bg-rose-50 border-rose-200 hover:border-rose-300' : 'bg-white border-gray-200'"
          (click)="lostOpen = true"
          matTooltip="Customers whose order is pending or was cancelled and who haven't bought since - click to follow them up">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [class]="lostTotals.count ? 'text-rose-500' : 'text-gray-400'">phone_callback</mat-icon>
          <span class="text-sm font-bold" [class]="lostTotals.count ? 'text-rose-700' : 'text-gray-700'">{{ lostTotals.count }}</span>
          <span class="text-sm" [class]="lostTotals.count ? 'text-rose-700/80' : 'text-gray-400'">lost sales</span>
          @if (lostTotals.count) { <span class="text-xs font-semibold text-rose-600/80">£{{ lostTotals.value | number:'1.0-0' }}</span> }
        </button>
        }
        <button
          class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:shadow-lg hover:shadow-amber-300 hover:from-amber-300 hover:to-amber-400 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-amber-200"
          (click)="onCreateSale()"
          matTooltip="Start a new sale">
          <span class="h-8 w-8 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform group-hover:rotate-90 duration-200">
            <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
          </span>
          New sale
        </button>
      </div>
    </div>

    <!-- Ideas for this hour: who to call, what to pitch -->
    <app-sales-ideas (openLeads)="openLeadPanel($event)" (openOrders)="statusOrdersOpen = $event"></app-sales-ideas>

    <!-- Search & filters -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">

      <!-- Smart search: works out what was typed -->
      <div class="flex flex-col lg:flex-row lg:items-end gap-3">
        <div class="relative flex-1">
          <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Search</label>
          <mat-icon class="absolute left-3 top-[38px] -translate-y-1/2 !text-gray-400 !h-5 !w-5 !text-[20px]">search</mat-icon>
          <input
            type="text"
            name="quickSearch"
            class="w-full pl-10 pr-36 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all text-sm"
            placeholder="Order no, customer, email, phone or postcode - add several with commas"
            [(ngModel)]="quickSearch"
            (keyup.enter)="onQuickSearch()"
          />
          @if (quickSearchKind; as kind) {
          <span class="absolute right-3 top-[38px] -translate-y-1/2 flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 pointer-events-none">
            <mat-icon class="!text-[13px] !h-[13px] !w-[13px]">{{ kind.icon }}</mat-icon>
            {{ kind.label }}
          </span>
          }
        </div>
        <div class="flex flex-row items-center gap-2 relative">
          <button
            class="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-semibold text-sm shadow-sm hover:shadow-md hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all"
            (click)="onQuickSearch()">
            Search
          </button>
          <button mat-raised-button color="accent" (click)="exportToExcel()" [disabled]="exporting">
            <mat-icon>table_view</mat-icon> {{ exporting ? 'Exporting...' : 'Export orders' }}
          </button>
        </div>
      </div>

      @if (searchOrderNo) {
      <div class="mt-3 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
        <mat-icon class="!text-[16px] !h-4 !w-4">info</mat-icon>
        Showing {{ orderNoCount === 1 ? 'order' : 'orders' }} {{ searchOrderNo }} only - the date and other filters are ignored for an order number search.
        <button class="ml-auto font-semibold underline hover:text-amber-900" (click)="clearOrderNo()">Back to all orders</button>
      </div>
      }

      <!-- Status -->
      <div class="mt-4 flex flex-wrap items-center gap-1" [class.opacity-40]="searchOrderNo" [class.pointer-events-none]="searchOrderNo">
        @for (st of mainStatuses; track st) {
        <button
          class="px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all"
          [ngClass]="status === st ? 'bg-neutral-900 text-amber-400' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'"
          (click)="setStatus(st)">
          {{ st === 'ALL' ? 'All' : (st | titlecase) }}
        </button>
        }
        <button
          class="flex items-center gap-0.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all"
          [ngClass]="isOtherStatus ? 'bg-neutral-900 text-amber-400' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'"
          [matMenuTriggerFor]="moreStatusMenu">
          {{ isOtherStatus ? (status | titlecase) : 'More' }}
          <mat-icon class="!text-[16px] !h-4 !w-4">expand_more</mat-icon>
        </button>
        <mat-menu #moreStatusMenu="matMenu">
          @for (st of otherStatuses; track st) {
          <button mat-menu-item (click)="setStatus(st)">
            <span [class.font-semibold]="status === st">{{ st | titlecase }}</span>
          </button>
          }
        </mat-menu>
      </div>

      <!-- Date range + dropdown filters -->
      <div class="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-3" [class.opacity-40]="searchOrderNo" [class.pointer-events-none]="searchOrderNo">
        <div class="flex flex-col lg:flex-row lg:items-center gap-3">
          <span class="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1">
            <mat-icon class="!text-[16px] !h-4 !w-4 !text-amber-500">event</mat-icon> Order date
          </span>
          <div class="inline-flex flex-wrap rounded-lg border border-gray-200 bg-gray-50 p-1 gap-1 w-fit">
            @for (preset of datePresets; track preset.key) {
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-semibold uppercase rounded-md transition-colors"
              [ngClass]="datePreset === preset.key ? 'bg-neutral-800 text-white' : 'text-gray-600 hover:bg-gray-200'"
              (click)="applyDatePreset(preset.key)">
              {{ preset.label }}
            </button>
            }
          </div>
          <div class="flex flex-row flex-wrap items-center gap-2 text-sm">
            <label class="text-xs font-medium text-gray-400 uppercase tracking-wide">From</label>
            <input type="date" name="fromDate" class="p-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-w-0" [(ngModel)]="fromDate" (change)="onCustomDateChange()" />
            <label class="text-xs font-medium text-gray-400 uppercase tracking-wide">To</label>
            <input type="date" name="toDate" class="p-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-w-0" [(ngModel)]="toDate" (change)="onCustomDateChange()" />
          </div>
        </div>

        <div class="flex flex-row flex-wrap items-end gap-3">
          <div class="w-48">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Payment</label>
            <select class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 outline-none text-sm" name="paymentMethod" [(ngModel)]="paymentMethod" (change)="applyFilters()">
              @for (pm of paymentMethods; track pm.value) {
              <option [value]="pm.value">{{ pm.label }}</option>
              }
            </select>
          </div>
          <div class="w-48">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Agent</label>
            <select class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 outline-none text-sm" name="agent" [(ngModel)]="agent" (change)="applyFilters()">
              <option [ngValue]="0">All agents</option>
              @for (ag of agents ?? []; track ag.userid) {
              <option [ngValue]="ag.userid">{{ ag.firstname }}</option>
              }
            </select>
          </div>
          <div class="w-48">
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Origin</label>
            <select class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 outline-none text-sm" name="origin" [(ngModel)]="origin" (change)="applyFilters()">
              @for (opt of originOptions; track opt) {
              <option [value]="opt">{{ opt ? originLabel(opt) : 'All origins' }}</option>
              }
            </select>
          </div>

          <button
            class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white text-sm font-semibold text-gray-700 transition-all"
            (click)="showMoreFilters = !showMoreFilters">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">tune</mat-icon>
            More filters
            @if (moreFilterCount) {
            <span class="min-w-[20px] text-center text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-neutral-900">{{ moreFilterCount }}</span>
            }
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">{{ showMoreFilters ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>

          @if (hasActiveFilters) {
          <button
            class="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-gray-500 hover:text-gray-800 hover:bg-gray-50 font-semibold text-sm transition-all"
            (click)="onResetFilters()">
            <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
            Clear all
          </button>
          }
        </div>

        @if (showMoreFilters) {
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-3 border-t border-dashed border-gray-100">
          @for (f of moreFields; track f.key) {
          <div>
            <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{{ f.label }}</label>
            <input
              type="text"
              class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none transition-all text-sm"
              [name]="f.key"
              [placeholder]="f.placeholder"
              [ngModel]="getField(f.key)"
              (ngModelChange)="setField(f.key, $event)"
              (keyup.enter)="applyFilters()" />
          </div>
          }
          <div class="sm:col-span-2 lg:col-span-3 flex justify-end">
            <button class="text-xs uppercase px-5 py-2.5 rounded-lg bg-neutral-800 text-white font-semibold hover:bg-neutral-700 transition-colors" (click)="applyFilters()">
              Apply filters
            </button>
          </div>
        </div>
        }
      </div>

      <!-- Active filters -->
      @if (activeChips.length) {
      <div class="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
        <span class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Filtering by</span>
        @for (c of activeChips; track c.key) {
        <span class="inline-flex items-center gap-1 text-xs font-semibold pl-3 pr-1.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
          {{ c.label }}: {{ c.value }}
          <button class="h-4 w-4 rounded-full hover:bg-amber-200 flex items-center justify-center" (click)="removeChip(c)" [attr.aria-label]="'Remove ' + c.label">
            <mat-icon class="!text-[12px] !h-3 !w-3">close</mat-icon>
          </button>
        </span>
        }
      </div>
      }
    </div>
    <!-- Search & filters -->

    <!-- Sales data table -->
    @if (orders.length > 0) {
    <div class="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="flex flex-row justify-between items-center px-5 py-4 border-b border-gray-100">
        <div class="flex items-baseline gap-3">
          <span class="font-semibold text-gray-800">Orders</span>
          <span class="text-xs text-gray-400">{{ rangeLabel }}</span>
        </div>
        <button
          class="flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700"
          (click)="loadOrders(pageNumber, itemsPerPage)"
          matTooltip="Refresh">
          <mat-icon class="!text-[20px]">refresh</mat-icon>
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">
              @for (c of sortableColumns; track c.key) {
              <th class="py-3 select-none" [ngClass]="c.cls">
                <button type="button" class="inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-gray-700 transition-colors"
                  [ngClass]="sortKey === c.key ? 'text-gray-800' : ''"
                  (click)="sortBy(c.key)">
                  {{ c.label }}
                  @if (sortKey === c.key) {
                  <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">{{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                  }
                </button>
              </th>
              }
              <th class="px-4 py-3">Sync</th>
              <th class="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            @for (o of rows; track o.orderId) {
            <tr class="group border-b border-gray-50 last:border-b-0 even:bg-gray-50/30 hover:bg-amber-50/40 cursor-pointer transition-colors align-top"
              (click)="onEdit(o)">

              <!-- Order -->
              <td class="px-5 py-4 whitespace-nowrap">
                <div class="flex items-start gap-3">
                  <div class="flex items-center justify-center h-9 w-9 rounded-xl bg-neutral-900 text-amber-400 font-bold text-[11px] flex-shrink-0">
                    #{{ o.orderNo?.toString()?.slice(-4) }}
                  </div>
                  <div>
                    <div class="font-semibold text-gray-900">#{{ o.orderNo }}</div>
                    <div class="text-xs text-gray-400">{{ o.orderDate | date: 'dd MMM yyyy, HH:mm' }}</div>
                    <div class="flex flex-wrap gap-1 mt-1.5">
                      <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {{ o.OrderItems?.length || 0 }} item{{ (o.OrderItems?.length || 0) === 1 ? '' : 's' }}
                      </span>
                      @if (originLabel(o.origin)) {
                      <span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700" matTooltip="Where the customer came from">
                        {{ originLabel(o.origin) }}
                      </span>
                      }
                    </div>
                  </div>
                </div>
              </td>

              <!-- Customer -->
              <td class="px-4 py-4 max-w-[190px]">
                <div class="text-gray-800 font-medium truncate" [matTooltip]="getCustomerName(o)">{{ getCustomerName(o) }}</div>
                @if (postCodeOf(o); as pc) {
                <a class="inline-flex items-center gap-1 text-xs text-gray-500 mt-1 px-1.5 py-0.5 -ml-1.5 rounded-md hover:bg-amber-100 hover:text-gray-900 transition-colors"
                  [href]="mapsUrl(o)" target="_blank" rel="noopener"
                  (click)="$event.stopPropagation()"
                  matTooltip="Open delivery address in Google Maps">
                  <mat-icon class="!text-[13px] !h-[13px] !w-[13px] !text-amber-500">place</mat-icon>
                  {{ pc }}
                  <mat-icon class="!text-[12px] !h-3 !w-3 opacity-60">open_in_new</mat-icon>
                </a>
                }
              </td>

              <!-- Status -->
              <td class="px-4 py-4 whitespace-nowrap">
                <span class="inline-block text-xs font-semibold px-2.5 py-1 rounded-full capitalize" [ngClass]="getStatusClass(o.orderStatus)">
                  {{ o.orderStatus | lowercase }}
                </span>
              </td>

              <!-- Payment: method + paid date -->
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="flex flex-col gap-1.5 items-start">
                  @if (o.paymentMethod) {
                  <span class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 w-fit capitalize">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">credit_card</mat-icon>
                    {{ o.paymentMethod | lowercase }}
                  </span>
                  }
                  @if (o.paymentDate) {
                  <div class="flex items-center gap-1.5 text-xs">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px] !text-gray-400">payments</mat-icon>
                    <span class="text-gray-600 font-medium">{{ o.paymentDate | date: 'dd MMM yyyy, HH:mm' }}</span>
                  </div>
                  } @else {
                  <span class="text-gray-300 text-xs">Not paid</span>
                  }
                </div>
              </td>

              <!-- Delivery: method + terms -->
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="flex flex-col gap-1.5 items-start">
                  @if (o.deliveryMethod) {
                  <span class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full w-fit"
                    [ngClass]="o.deliveryMethod === 'COLLECTION' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">{{ o.deliveryMethod === 'COLLECTION' ? 'storefront' : 'local_shipping' }}</mat-icon>
                    {{ deliveryMethodLabel(o) }}
                  </span>
                  }
                  @if (deliveryTermsShort(o); as terms) {
                  <span class="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 w-fit" [matTooltip]="o.deliveryTerms">
                    <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">schedule</mat-icon>
                    {{ terms }}{{ terms === 'NaN' ? '' : ' days' }}
                  </span>
                  }
                  @if (!o.deliveryMethod && !deliveryTermsShort(o)) {
                  <span class="text-gray-300 text-xs">-</span>
                  }
                </div>
              </td>

              <!-- Amount -->
              <td class="px-4 py-4 whitespace-nowrap text-right">
                <div class="font-semibold text-gray-900">£{{ o.netAmount | number:'1.2-2' }}</div>
                @if (o.refundTotal > 0) {
                <div class="text-xs font-semibold text-red-600 mt-0.5">- £{{ o.refundTotal | number:'1.2-2' }} refunded</div>
                <div class="text-xs text-gray-500 mt-0.5">Net £{{ (o.netAmount - o.refundTotal) | number:'1.2-2' }}</div>
                }
              </td>

              <!-- Agent -->
              <td class="px-4 py-4 max-w-[130px]">
                @if (o.agent && o.customerId !== o.userId) {
                <div class="flex items-center gap-2">
                  <div class="h-7 w-7 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                    {{ (o.agent.firstname || '?')[0] | uppercase }}
                  </div>
                  <span class="text-gray-700 text-xs font-medium truncate">{{ o.agent.firstname }}</span>
                </div>
                } @else {
                <span class="text-gray-300 text-xs">-</span>
                }
              </td>

              <!-- Sync -->
              <td class="px-4 py-4 whitespace-nowrap" (click)="$event.stopPropagation()">
                @if (o.orderSync) {
                <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold bg-green-50 text-green-700">
                  <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">check_circle</mat-icon> Synced
                </span>
                } @else if (o.orderStatus === 'PROCESSING') {
                <button
                  class="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 transition-colors text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm"
                  (click)="syncOrder(o.orderId)"
                  matTooltip="Sync this order to Odoo">
                  <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">autorenew</mat-icon> Sync
                </button>
                } @else {
                <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-500">
                  <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">hourglass_empty</mat-icon> Pending
                </span>
                }
              </td>

              <!-- Action -->
              <td class="px-5 py-4 whitespace-nowrap">
                <button class="flex items-center gap-1 px-4 py-2 rounded-xl border border-amber-300 text-amber-600 font-semibold text-xs group-hover:bg-amber-400 group-hover:text-neutral-900 group-hover:border-amber-400 transition-all">
                  Edit
                  <mat-icon class="!text-[16px] !h-4 !w-4">arrow_forward</mat-icon>
                </button>
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Paginator -->
      <mat-paginator [pageSizeOptions]="[10, 20, 50]"
                     showFirstLastButtons
                     [length]="count"
                     [pageSize]="itemsPerPage"
                     [pageIndex]="pageNumber"
                     (page)="updateList($event)">
      </mat-paginator>
    </div>
    }
    <!-- Sales data table -->

    <!-- No orders to display -->
    @if (orders.length == 0) {

      <div class="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 min-h-80 flex flex-col justify-center items-center">
        <mat-icon class="!text-5xl !h-12 !w-12 text-neutral-300 mb-2">search_off</mat-icon>
        <div class="text-xl font-bold text-neutral-700">No orders found</div>
        <div class="text-base font-normal text-neutral-500 mt-1">
          Try adjusting your search or filters
        </div>
      </div>

    }
    <!-- No orders to display -->

  </div>
  </div>

  @if (cartsOpen) {
    <app-abandoned-carts [initialDays]="30" (closed)="cartsOpen = false" (totalsChanged)="cartTotals = $event"></app-abandoned-carts>
  }

  @if (samplesOpen) {
    <app-lost-sales mode="samples" [initialDays]="30" (closed)="samplesOpen = false" (totalsChanged)="sampleTotals = $event" (switchMode)="samplesOpen = false; lostOpen = true"></app-lost-sales>
  }

  @if (statusOrdersOpen) {
    <app-order-activity [status]="statusOrdersOpen" range="today" (closed)="statusOrdersOpen = null"></app-order-activity>
  }

  @if (lostOpen) {
    <app-lost-sales [initialDays]="90" (closed)="lostOpen = false" (totalsChanged)="lostTotals = $event" (switchMode)="lostOpen = false; samplesOpen = true"></app-lost-sales>
  }
</app-main-layout>
